/**
 * @file Shared employee-picker modal. Search by name or email, click a row
 * to confirm, callback receives the chosen `/employees` row. Used by the
 * device-detail Transfer modal and the Data-quality "Link form submission"
 * action — both need the same UX.
 *
 * Caches `/employees` in a MODULE-level map (deliberately not on `window` —
 * it holds admin-only PII like homeEmail, so it shouldn't be reachable from
 * a console in a shared browser). Cleared on sign-out and after a test-mode
 * reseed via clearEmployeePickerCache(). With ~750 rows the filter happens
 * client-side and is instant.
 */

import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase.js';
import { el, clear } from './utils.js';
import { testCollectionName, isTestMode } from './test-mode.js';
import { PHASES } from './config.js';

// "Departed" = offboarding finished (phase `done`). These stay selectable —
// you might sell a device to someone on their way out — but they're sorted
// below current staff and greyed, so picking a buyer isn't drowned in former
// employees who happen to share a surname.
const isDeparted = (e) => e.phase === PHASES.done;

// prod / test caches kept apart so the sandbox never serves production rows.
const employeeCache = { prod: null, test: null };

/** Drop the cached employee list (sign-out, or after a sandbox reseed). */
export function clearEmployeePickerCache() {
  employeeCache.prod = null;
  employeeCache.test = null;
}

/**
 * @param opts.title        modal heading text
 * @param opts.subtitle     short explainer below title
 * @param opts.confirmLabel button text for the confirm action
 * @param opts.onPick       async (employee) => void — called when admin confirms
 * @param opts.extraSlot    optional DOM node to render between picker and buttons
 * @param opts.initialQuery optional search to run on open; an exact email match
 *                          is pre-selected (e.g. the MDM owner), so confirming
 *                          is one tap
 */
export function openEmployeePicker(opts) {
  const {
    title = 'Pick an employee',
    subtitle = '',
    confirmLabel = 'Confirm',
    onPick,
    extraSlot = null,
    initialQuery = '',
  } = opts;

  const backdrop  = el('div', { class: 'modal-backdrop open' });
  const search    = el('input', { type: 'search', placeholder: 'Search by name or email...' });
  const results   = el('div', { class: 'stack', style: 'gap:4px;max-height:240px;overflow:auto;margin-top:8px' });

  let chosen = null;
  let debounceTimer = null;
  let confirmBtn = null; // assigned when the modal is built; enabled on selection

  async function lookup(q) {
    clear(results);
    const needle = q.trim().toLowerCase();
    if (!needle || needle.length < 2) return;
    // Separate prod/test buckets so the sandbox never serves production rows.
    const bucket = isTestMode() ? 'test' : 'prod';
    if (!employeeCache[bucket]) {
      const snap = await getDocs(collection(db, testCollectionName('employees')));
      employeeCache[bucket] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    }
    const matches = employeeCache[bucket]
      .filter((e) => {
        const hay = `${e.fullName || ''} ${e.email || ''} ${e.homeEmail || ''}`.toLowerCase();
        return hay.includes(needle);
      })
      // Current staff first, departed last; alphabetical within each group.
      // Sort BEFORE the slice so a common surname can't push current
      // employees out of the top-10 behind former ones.
      .sort((a, b) => {
        const ra = isDeparted(a) ? 1 : 0;
        const rb = isDeparted(b) ? 1 : 0;
        if (ra !== rb) return ra - rb;
        return (a.fullName || '').localeCompare(b.fullName || '');
      })
      .slice(0, 10);
    for (const e of matches) {
      const departed = isDeparted(e);
      const row = el('button', {
        class: departed ? 'picker-row picker-row-inactive' : 'picker-row',
        onClick: () => {
          chosen = e;
          for (const r of results.querySelectorAll('.picker-row')) r.classList.remove('selected');
          row.classList.add('selected');
          if (confirmBtn) { confirmBtn.disabled = false; confirmBtn.textContent = `${confirmLabel} — ${e.fullName || e.email}`; }
        },
      },
        el('span', { class: 'picker-row-check', 'aria-hidden': 'true' }, '✓'),
        el('span', { class: 'picker-row-name' }, e.fullName || '(no name)'),
        el('span', { class: 'picker-row-email muted' }, e.email || e.homeEmail || ''),
      );
      results.appendChild(row);
    }
  }

  search.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => lookup(search.value), 180);
  });

  const close = () => backdrop.remove();
  const confirm = async () => {
    if (!chosen) return;
    confirmBtn.disabled = true;
    confirmBtn.replaceChildren(el('span', { class: 'spinner' }));
    try {
      await onPick(chosen);
      close();
    } catch (err) {
      console.error('[employee-picker] onPick failed', err);
      confirmBtn.disabled = false;
      confirmBtn.textContent = confirmLabel;
    }
  };

  // Confirm is disabled until a row is picked — the selection IS the
  // affordance (the old version toggled an `.active` class with no CSS,
  // so picking looked like nothing happened).
  confirmBtn = el('button', { class: 'btn', onClick: confirm }, confirmLabel);
  confirmBtn.disabled = true;

  backdrop.appendChild(el('div', { class: 'modal' },
    el('h2', null, title),
    subtitle ? el('p', { class: 'muted', style: 'margin-top:0' }, subtitle) : null,
    el('div', { class: 'field', style: 'margin-top:16px' },
      el('label', null, 'Search'), search),
    results,
    extraSlot,
    el('div', { class: 'row', style: 'margin-top:24px;justify-content:flex-end' },
      el('button', { class: 'btn-ghost btn', onClick: close }, 'Cancel'),
      confirmBtn,
    ),
  ));
  document.body.appendChild(backdrop);
  search.focus();
  if (initialQuery) {
    search.value = initialQuery;
    lookup(initialQuery).then(() => {
      const exact = [...results.querySelectorAll('.picker-row')]
        .find((r) => r.querySelector('.picker-row-email')?.textContent.trim().toLowerCase() === initialQuery.toLowerCase());
      exact?.click();
    }).catch((err) => console.error('[employee-picker] initial search failed', err));
  }
}
